import React, {useContext, useEffect, useState, useMemo} from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { PriceContext } from './context';
import { PredContext } from './sportsc/predContext';
import PlayerPage from './playerPage';
import CreditCounter from './sportsc/CreditCounter';
import axios from 'axios';
import useTeamStore from './config/useTeamStore'
// Global styles for body
import TeamData from './TeamDate';
import LinesResultsView from './LinesResultView';
// --- LINES VIEW STYLED COMPONENTS ---

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
`;

const ActionButton = styled.button`
  padding: 8px 14px;
  font-size: 0.825rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ClearButton = styled(ActionButton)`
  background-color: #ef4444;
  color: white;
`;

const SendButton = styled(ActionButton)`
  background-color: #16a34a;
  color: white;
`;

const LinesButton = styled.button`
  background-color: #0e141b;
  color: white;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  margin: 1rem 1rem 0.5rem;
  transition: all 0.2s ease;
  width: calc(100% - 2rem);
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background-color: #1e293b;
  }
`;

const LinesContainer = styled.div`
  background: white;
  margin: 0 1rem 1rem;
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 350px;
  overflow-y: auto;
`;

const LinesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.875rem;

  th {
    padding: 8px;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    border-bottom: 2px solid #e2e8f0;
  }

  td {
    padding: 10px 8px;
    border-bottom: 1px solid #f1f5f9;
    color: #0f172a;
    font-weight: 500;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const Badge = styled.span`
  background: #e2e8f0;
  color: #0e141b;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 6px;
  text-transform: uppercase;
`;

const EmptyText = styled.p`
  color: #64748b;
  font-size: 0.875rem;
  text-align: center;
  margin: 1rem 0;
`;
const ClickableCell = styled.td`
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #1d4ed8;
  }
`;
// --- LINES VIEW COMPONENT ---

function LinesView({ onSendLines }) {
  const { lines, clearLines } = useContext(PredContext);

  // 1. STATE FOR CONTROL
  const [responseData, setResponseData] = useState(null);
  const [loading, setLoading] = useState(false);
 const [selectedPlayerObj, setSelectedPlayerObj] = useState(null);
  // Format existing context lines
  const formattedLines = useMemo(() => {
    if (!lines) return [];
    const result = [];
    Object.entries(lines).forEach(([statType, playersMap]) => {
      if (!playersMap) return;
      Object.entries(playersMap).forEach(([player, data]) => {
        result.push({
          player,
          type: statType,
          line: data.line,
          team: data.team ? data.team.replace(/\.csv$/, '') : 'N/A',
        });
      });
    });
    return result;
  }, [lines]);

  // 2. FETCH LOGIC
  const fetchLines = async (linesPayload) => {
    setLoading(true);
    try {
      const res = await axios({
        method: 'post',
        url: `https://sx7vpiy3ij.execute-api.us-east-2.amazonaws.com/admin_dev/team-pull`,
        withCredentials: false,
        data: {
          option: 'lines',
          players: linesPayload,
        },
      });

      // Populate returned array
      if (res.data && res.data.data) {
        setResponseData(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch lines:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    if (onSendLines) {
      onSendLines(formattedLines);
    } else {
      fetchLines(formattedLines);
    }
  };

  const handleReset = () => {
    setResponseData(null);
  };

  // 3. CONDITIONAL RENDER: LOADING STATE
  if (loading) {
    return (
      <LinesContainer>
        <EmptyText>Fetching predictions...</EmptyText>
      </LinesContainer>
    );
  }

  // 4. CONDITIONAL RENDER: SHOW RESULTS COMPONENT ONLY WHEN DATA IS LOADED
  if (responseData) {
    return <LinesResultsView data={responseData} onReset={handleReset} />;
  }

  // 5. DEFAULT STATE: INPUT TABLE
  if (formattedLines.length === 0) {
    return (
      <LinesContainer>
        <EmptyText>No active lines submitted yet.</EmptyText>
      </LinesContainer>
    );
  }
  if (selectedPlayerObj) {
    return (
      <div>
      <PlayerPage
        player={selectedPlayerObj.player}
        view={selectedPlayerObj.view}
        team={selectedPlayerObj.team}
        onBack={() => setSelectedPlayerObj(null)}
      />
      </div>
    );
  }
  return (
    <LinesContainer>
      <LinesTable>
        <thead>
          <tr>
            <th>Player</th>
            <th>Type</th>
            <th>Line</th>
            <th>Team</th>
          </tr>
        </thead>
        <tbody>
          {formattedLines.map((item, idx) => (
            <tr key={`${item.player}-${item.type}-${idx}`}>
              <td style={{ fontWeight: 600 }}>
                
            <ClickableCell
                onClick={() =>
                  setSelectedPlayerObj({
                    player: item.player,
                    view: item.type.toLowerCase(),
                    team: item.team,
                  })
                }
              >
                {item.player}
              </ClickableCell>
                
                </td>
              <td><Badge>{item.type}</Badge></td>
              <td style={{ fontWeight: 700, color: '#16a34a' }}>{item.line}</td>
              <td>{item.team}</td>
            </tr>
          ))}
        </tbody>
      </LinesTable>

      <ActionRow>
        <ClearButton onClick={clearLines}>Clear Lines</ClearButton>
        <SendButton onClick={handleSend}>Send Lines</SendButton>
      </ActionRow>
    </LinesContainer>
  );
}



const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    font-family: Inter, "Noto Sans", sans-serif;
  }
`;

// Styled components
const CContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8fafc; /* bg-slate-50 */
  justify-content: space-between;
  overflow-x: auto;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  background-color: #f8fafc; /* bg-slate-50 */
  padding: 1rem; /* p-4 */
  padding-bottom: 0.5rem; /* pb-2 */
  justify-content: space-between;
`;

const BackButton = styled.div`
  color: #0d141c;
  display: flex;
  width: 3rem; /* size-12 */
  height: 3rem; /* size-12 */
  flex-shrink: 0;
  align-items: center;

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }
`;

const Title = styled.h2`
  color: #0d141c;
  font-size: 1.125rem; /* text-lg */
  font-weight: bold;
  line-height: 1.25; /* leading-tight */
  letter-spacing: -0.015em;
  flex: 1;
  text-align: center;
  padding-right: 3rem; /* pr-12 */
`;

const UserEmailSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem; /* gap-4 */
  background-color: #f8fafc; /* bg-slate-50 */
  padding-left: 1rem; /* px-4 */
  padding-right: 1rem; /* px-4 */
  min-height: 3.5rem; /* min-h-14 */
`;

const UserEmail = styled.p`
  color: #0d141c;
  font-size: 1rem; /* text-base */
  font-weight: normal;
  line-height: 1.5rem; /* leading-normal */
  flex: 1;
  
  text-overflow: ellipsis;
  white-space: nowrap; /* truncate */
`;

const SectionTitle = styled.h2`
  color: #0d141c;
  font-size: 1.375rem; /* text-[22px] */
  font-weight: bold;
  line-height: 1.25; /* leading-tight */
  letter-spacing: -0.015em;
  padding-left: 1rem; /* px-4 */
  padding-right: 1rem; /* px-4 */
  padding-bottom: 0.75rem; /* pb-3 */
  padding-top: 1.25rem; /* pt-5 */
`;

const SubscriptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem; /* gap-4 */
  background-color: #f8fafc; /* bg-slate-50 */
  padding-left: 1rem; /* px-4 */
  padding-right: 1rem; /* px-4 */
  min-height: 4.5rem; /* min-h-[72px] */
  padding-top: 0.5rem; /* py-2 */
  padding-bottom: 0.5rem; /* py-2 */
`;

const IconContainer = styled.div`
  color: #0d141c;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem; /* rounded-lg */
  background-color: #e7edf4;
  flex-shrink: 0;
  width: 3rem; /* size-12 */
  height: 3rem; /* size-12 */

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }
`;

const SubscriptionTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const SubscriptionPlan = styled.p`
  color: #0d141c;
  font-size: 1rem; /* text-base */
  font-weight: 500; /* font-medium */
  line-height: 1.5rem; /* leading-normal */
  
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1; /* line-clamp-1 */
`;

const SubscriptionExpiry = styled.p`
  color: #49739c;
  font-size: 0.875rem; /* text-sm */
  font-weight: normal;
  line-height: 1.5rem; /* leading-normal */
  
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; /* line-clamp-2 */
`;

const BottomNav = styled.nav`
  display: flex;
  gap: 0.5rem; /* gap-2 */
  border-top: 1px solid #e7edf4;
  background-color: #f8fafc; /* bg-slate-50 */
  padding-left: 1rem; /* px-4 */
  padding-right: 1rem; /* px-4 */
  padding-bottom: 0.75rem; /* pb-3 */
  padding-top: 0.5rem; /* pt-2 */
`;

const NavLink = styled.a`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem; /* gap-1 */
  color: #49739c;
  text-decoration: none;
`;

const NavLinkIcon = styled.div`
  color: #49739c;
  display: flex;
  height: 2rem; /* h-8 */
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }
`;

const NavLinkText = styled.p`
  color: #49739c;
  font-size: 0.75rem; /* text-xs */
  font-weight: 500; /* font-medium */
  line-height: 1rem; /* leading-normal */
  letter-spacing: 0.015em;
`;


const SubscriptionItemList = ({ subscription }) => {
  const dateString = subscription.subscription_end;
  console.log(dateString)
  return (
          <SubscriptionItem>
            <IconContainer>
              <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" fill="currentColor" viewBox="0 0 256 256">
                <path
                  d="M239.2,97.29a16,16,0,0,0-13.81-11L166,81.17,142.72,25.81h0a15.95,15.95,0,0,0-29.44,0L90.07,81.17,30.61,86.32a16,16,0,0,0-9.11,28.06L66.61,153.8,53.09,212.34a16,16,0,0,0,23.84,17.34l51-31,51.11,31a16,16,0,0,0,23.84-17.34l-13.51-58.6,45.1-39.36A16,16,0,0,0,239.2,97.29Zm-15.22,5-45.1,39.36a16,16,0,0,0-5.08,15.71L187.35,216v0l-51.07-31a15.9,15.9,0,0,0-16.54,0l-51,31h0L82.2,157.4a16,16,0,0,0-5.08-15.71L32,102.35a.37.37,0,0,1,0-.09l59.44-5.14a16,16,0,0,0,13.35-9.75L128,32.08l23.2,55.29a16,16,0,0,0,13.35,9.75L224,102.26S224,102.32,224,102.33Z"
                ></path>
              </svg>
            </IconContainer>
            <SubscriptionTextContainer>
              <SubscriptionPlan>{subscription.subscription_type}</SubscriptionPlan>
              <SubscriptionExpiry><bold>End Date:</bold>  {subscription.subscription_end}</SubscriptionExpiry>
                            <SubscriptionExpiry>{subscription.teams}</SubscriptionExpiry>

            </SubscriptionTextContainer>
          </SubscriptionItem>
  );
};




const Account = () => {
const teamList = useTeamStore((state) => state.teams);
  const setTeams = useTeamStore((state) => state.setTeams);
    const {email, subs, setSubs, setSubTeams, setCredit, isAuthenticated} = useContext(PriceContext);
    const [showLines, setShowLines] = useState(false);
    const [showSubscriptions, setShowSubscriptions] = useState(false); // Add this line


  const predCheck = async ()  => {
    console.log('running check')
    await axios({
        method: 'post',
        url: `https://dhqs56xckk.execute-api.us-east-2.amazonaws.com/pred_dev/load`,
       
        withCredentials: false,
        data: {
        email:email,
        path:'team-data'
      },
      }).then(function (response) {
         const data = response.data;
         console.log('team  player data test load')
        console.log(data)
      
  
      })
      .catch(function (error) {
      });
  } 
function removeDuplicates(arr) {
    return [...new Set(arr)];
}

const runUpdate =  (t) => {

  console.log('ypdate startubg')
  console.log('t', t)
  setTeams(t)
    // Ensure you are passing an array!
  console.log('e teams', teamList)
  };

  async function grabCredits() {
    console.log('trying to get credits')
    try {
      axios({
        method: 'post',
        url: `https://82jsvqje43.execute-api.us-east-2.amazonaws.com/wnba_dev/credits`,
       
        withCredentials: false,
        data: {
          email:email,
          path:'user-credit-load'
        },
      }).then(function (response) {
         const data = response.data;
         console.log('credits left')
        let credits_purchased = Number(data);
        console.log('le credits', credits_purchased)
        setCredit(credits_purchased);

      })
      .catch(function (error) {
      });



    } catch (error) {
        console.error('Error:', error);
    }
}



function getUniqueTeams(teamList) {
  console.log('lions')
  console.log(teamList)
  const allTeams = teamList.flatMap(str => str.split(','));
  const uniqueTeams = [...new Set(allTeams)];
  return uniqueTeams;
}

    // function to grab teams list
function subscribedTeams(subs) {
  const sub_teams = []
  const now = new Date();

  for (const item of subs) {
    const subscriptionEnd = new Date(item.subscription_end);

    if (subscriptionEnd < now) {
      const timeDiff = now.getTime() - subscriptionEnd.getTime();
      const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      const hoursDiff = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

      console.log('---------------------------------');
      console.log(`Subscription for item ${item.subscription_end} has expired.`);
      console.log(`It expired approximately ${daysDiff} days and ${hoursDiff} hours ago.`);
    } else {
      console.log('---------------------------------');
      console.log(`Subscription for item ${item.subscription_id} is still active.`);
      sub_teams.push(item.teams)

    }
  }
const non_dup_teams = removeDuplicates(sub_teams)
const full_non_dup_teams = getUniqueTeams(non_dup_teams)
console.log('the non dup teams', full_non_dup_teams)
return full_non_dup_teams
}

  // pred ccheck

    // Function to fetch data from an API
    const fetchSubscriptions = async () => {

      
    // grabCredits()
      await axios({
        method: 'post',
        url: `https://82jsvqje43.execute-api.us-east-2.amazonaws.com/wnba_dev/credits`,
       
        withCredentials: false,
        data: {
          email:email,
          path:'grab-sub',

        },
      }).then(function (response) {
         const data = response.data;
         console.log('sub data')
        console.log(data)
        if(data.length >1){
          return
        }
        setSubs(data)
      
        console.log('the subs', subs)
        let team_list = subscribedTeams(data)
        setSubTeams(team_list)
        console.log('this is teams', team_list)
        runUpdate(team_list)
      })
      .catch(function (error) {
      });
    }

    // Call the function when the component mounts
    useEffect(() => {
      predCheck()
        fetchSubscriptions();
    }, [isAuthenticated,teamList]);

    subscribedTeams(subs)
  return (
    <>
      <GlobalStyle />
      <CContainer>
        <div>
          <Header>

            <Title>Account</Title>
          </Header>

          <UserEmailSection>
            <UserEmail>{email}</UserEmail>
          </UserEmailSection>

          {/* <CreditCounter/> */}

          <SectionTitle>Subscription History</SectionTitle>
<LinesButton onClick={() => setShowSubscriptions((prev) => !prev)}>
  <span>{showSubscriptions ? 'Hide Subscriptions' : 'View Subscriptions'}</span>
  <span>{showSubscriptions ? '▲' : '▼'}</span>
</LinesButton>

{showSubscriptions && (
  <div>
    {subs.length === 0 ? (
      <EmptyText>No active subscriptions found.</EmptyText>
    ) : (
      [...subs].reverse().map((subscription, index) => (
        <SubscriptionItemList 
          key={subscription.id || index} 
          subscription={subscription} 
        />
      ))
    )}
  </div>
)}
          <LinesButton onClick={() => setShowLines((prev) => !prev)}>
        <span>{showLines ? 'Hide Submitted Lines' : 'View Submitted Lines'}</span>
        <span>{showLines ? '▲' : '▼'}</span>
      </LinesButton>

{showLines && <LinesView />}

       <TeamData></TeamData>

        </div>

      </CContainer>
    </>
  );
};

export default Account;